import React, { useState, useMemo, useCallback } from 'react';
import {
  List,
  Datagrid,
  TextField,
  ReferenceField,
  EditButton,
  ShowButton,
  TopToolbar,
  FunctionField,
  SearchInput,
} from 'react-admin';
import { Card, Typography, Box, Button } from '@mui/material';
import {
  Add as AddIcon,
  LocationOn as ZoneIcon,
  Factory as PlantIcon,
  ListAlt as ListAltIcon,
} from '@mui/icons-material';
import HierarchicalFilter, {
  HierarchicalFilterSelections,
} from '../../components/HierarchicalFilter';
import DeleteWithPasswordButton from '@/components/DeleteWithPasswordButton';
import CustomCreateButton from '@/components/CustomCreateButton';

const ZoneListActions = () => (
  <TopToolbar>
    <CustomCreateButton
      variant='contained'
      label='Crear Zona'
      icon={<AddIcon />}
      sx={{
        height: '40px',
        backgroundColor: 'primary.main',
        '&:hover': {
          backgroundColor: 'primary.dark',
        },
      }}
    />
  </TopToolbar>
);

const zoneFilters = [
  <SearchInput
    key='search'
    source='q'
    placeholder='Buscar zonas...'
    alwaysOn
    size='small'
    sx={{ 
      minWidth: 200,
      mb: 0.5
    }}
  />,
];

export const ZonesList = () => {
  const [filterValues, setFilterValues] = useState<HierarchicalFilterSelections>({});
  const [pagination, setPagination] = useState({ page: 1, perPage: 5 }); 

  // ✅ USAR useCallback COMO EN DevicesList
  const handleFilterChange = useCallback((selections: HierarchicalFilterSelections) => {
    console.log('🔍 HierarchicalFilter onChange:', selections);
    setFilterValues(selections);
    setPagination(prev => ({ ...prev, page: 1 })); // ✅ RESET PAGINACIÓN
  }, []);

  // ✅ AGREGAR PAGINACIÓN SERVIDOR
  const filter = useMemo(() => {
    const filterObj: any = {};

    if (filterValues.plantId) {
      filterObj.plantId = filterValues.plantId;
    }

    // ✅ PAGINACIÓN SERVIDOR - IGUAL QUE DevicesList
    filterObj.isPaginated = true;
    filterObj.page = pagination.page;
    filterObj.pageSize = pagination.perPage;

    console.log('🔍 ZonesList filter built:', filterObj);
    return filterObj;
  }, [filterValues.plantId, pagination.page, pagination.perPage]); // ✅ AGREGAR PAGINACIÓN A DEPENDENCIAS

  return (
    <Card elevation={1} sx={{ m: 2, p: 2 }}>
      <Box
        sx={{ p: 3, pt: 2, pb: 2.5, borderBottom: 1, borderColor: 'divider' }}
      >
        <Typography
          variant='h5'
          component='h1'
          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
        >
          <ZoneIcon color='primary' />
          Gestión de Zonas
        </Typography>
        <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
          Gestiona las zonas del sistema organizadas por plantas
        </Typography>
      </Box>

      {/* Filtro Jerárquico */}
      <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant='h6' gutterBottom color='primary'>
          Filtros de Búsqueda
        </Typography>
        <HierarchicalFilter
          levels={['plant']}
          onChange={handleFilterChange}
          size='medium'
        />

        {filterValues.plantId && (
          <Box sx={{ mt: 2 }}>
            <Typography variant='body2' color='text.secondary'>
              ✅ Filtro aplicado: Planta: {filterValues.plantName}
            </Typography>
          </Box>
        )}

        {/* ✅ AGREGAR INDICADOR CUANDO NO HAY FILTROS */}
        {!filterValues.plantId && (
          <Box sx={{ mt: 2 }}>
            <Typography variant='body2' color='info.main'>
              ✓ Mostrando todas las zonas del sistema
            </Typography>
          </Box>
        )}
      </Box>

      {/* ✅ AGREGAR perPage DINÁMICO */}
      <List
        title={false}
        actions={<ZoneListActions />}
        filter={filter}
        filters={zoneFilters}
        perPage={pagination.perPage} // ✅ USAR perPage DEL STATE
        sort={{ field: 'zoneName', order: 'ASC' }}
        exporter={false}
        sx={{ 
          '& .RaList-main': { mt: 0, p: 0 },
          '& .RaDatagrid-rowCell, & .RaDatagrid-headerCell': {
            padding: '6px 8px',
          }
        }}
      >
        <Box sx={{
          width: '100%',
          overflowX: 'auto',
          pb: 1,
        }}>
          <Datagrid
            rowClick='show'
            sx={{ 
              '& .RaDatagrid-headerCell': { fontWeight: 'bold' },
              tableLayout: 'fixed',
              width: '100%',
            }}
          >
            <TextField source='id' label='ID' sx={{ minWidth: 50, maxWidth: 50 }} />
            <TextField source='zoneName' label='Nombre de la Zona' sx={{ maxWidth: 200 }} />
            
            <ReferenceField
              source='plantId'
              reference='plants'
              label='Planta'
              link='show'
              sx={{ maxWidth: 150 }}
            >
              <FunctionField
                render={(record: any) => {
                  if (!record) return 'Sin planta';
                  return (
                    <Box display='flex' alignItems='center' gap={1}>
                      <PlantIcon color='action' fontSize='small' />
                      <span>
                        {record.plantName || record.name || 'Planta sin nombre'}
                      </span>
                    </Box>
                  );
                }}
              />
            </ReferenceField>
            
            <FunctionField
              label='Variables'
              render={(record: any) => (
                <Button 
                  variant='outlined'
                  size='small'
                  startIcon={<ListAltIcon />}
                  onClick={() => {
                    console.log('🔍 Zone Variables Button clicked:', {
                      zoneId: record.id,
                      zoneName: record.zoneName,
                      plantId: record.plantId
                    });
                    window.location.href = `/#/variables?filter={"zoneId":${record.id}}`;
                  }}
                  sx={{ 
                    textTransform: 'none', 
                    whiteSpace: 'nowrap',
                    fontSize: '0.7rem' 
                  }}
                >
                  Ver Variables
                </Button>
              )}
              sx={{ maxWidth: 120 }}
            />
            
            <ShowButton label='' />
            <EditButton label=''/>
            <DeleteWithPasswordButton resource='zones' label='' />
          </Datagrid>
        </Box>
      </List>
    </Card>
  );
};

export default ZonesList;